import { Lead, LeadFilters, LeadStatus, LeadOutcome } from "../types/LeadTypes";
import { parseLeadDate } from "./dateFilters";

/**
 * Checks if a lead matches text search criteria
 */
export const matchesSearchTerm = (lead: Lead, searchTerm: string): boolean => {
  if (!searchTerm) return true;

  const searchLower = searchTerm.toLowerCase();
  return (
    lead.name.toLowerCase().includes(searchLower) ||
    lead.phone.toLowerCase().includes(searchLower)
  );
};

/**
 * Checks if a lead matches quality filter criteria
 */
export const matchesQualityFilter = (
  lead: Lead,
  leadQuality: string[]
): boolean => {
  // If no quality filters selected, return false
  if (leadQuality.length === 0) return false;

  // If both qualified and disqualified selected (default state), show all leads
  if (
    leadQuality.length === 2 &&
    leadQuality.includes("qualified") &&
    leadQuality.includes("disqualified")
  ) {
    return true;
  }

  // A qualified outcome is anything that doesn't start with "Disqualified" and is not "Existing Customer"
  const isQualified = lead.outcome
    ? !lead.outcome.startsWith("Disqualified") &&
      lead.outcome !== "Existing Customer"
    : false;

  // A disqualified outcome is anything that starts with "Disqualified" or is "Existing Customer"
  const isDisqualified = lead.outcome
    ? lead.outcome.startsWith("Disqualified") ||
      lead.outcome === "Existing Customer"
    : false;

  // If only qualified is selected
  if (
    leadQuality.includes("qualified") &&
    !leadQuality.includes("disqualified")
  ) {
    return isQualified;
  }

  // If only disqualified is selected
  if (
    leadQuality.includes("disqualified") &&
    !leadQuality.includes("qualified")
  ) {
    return isDisqualified;
  }

  return true;
};

/**
 * Checks if a lead matches status filter criteria
 */
export const matchesStatusFilter = (
  lead: Lead,
  statuses: LeadStatus[]
): boolean => {
  if (statuses.length === 0) return true;
  return statuses.includes(lead.status);
};

/**
 * Checks if a lead matches outcome filter criteria
 */
export const matchesOutcomeFilter = (
  lead: Lead,
  outcomes: LeadOutcome[]
): boolean => {
  if (outcomes.length === 0) return true;
  return lead.outcome ? outcomes.includes(lead.outcome) : false;
};

/**
 * Checks if a lead matches type filter criteria
 */
export const matchesTypeFilter = (lead: Lead, types: string[]): boolean => {
  if (types.length === 0) return true;
  return types.includes(lead.type);
};

/**
 * Checks if a lead matches value range filter criteria
 */
export const matchesValueFilter = (
  lead: Lead,
  minValue: string,
  maxValue: string
): boolean => {
  const leadValue = lead.value.trim();

  if (!minValue && !maxValue) return true;

  // If lead has no value but we have min/max filters
  if (leadValue === "" || leadValue === "$") {
    return false;
  }

  const numValue = leadValue
    ? parseFloat(leadValue.replace(/[^0-9.-]+/g, ""))
    : 0;

  if (minValue && !isNaN(parseFloat(minValue))) {
    if (numValue < parseFloat(minValue)) {
      return false;
    }
  }

  if (maxValue && !isNaN(parseFloat(maxValue))) {
    if (numValue > parseFloat(maxValue)) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if a lead matches date range filter criteria
 */
export const matchesDateFilter = (
  lead: Lead,
  from: Date | undefined,
  to: Date | undefined
): boolean => {
  if (!from && !to) return true;

  const leadDate = parseLeadDate(lead.date);

  if (!leadDate) {
    return false;
  }

  if (from && leadDate < from) {
    return false;
  }

  if (to) {
    const endDate = new Date(to);
    endDate.setHours(23, 59, 59, 999); // End of day
    if (leadDate > endDate) {
      return false;
    }
  }

  return true;
};

/**
 * Applies all filters to a lead
 */
export const applyFiltersToLead = (
  lead: Lead,
  filters: LeadFilters,
  searchTerm: string
): boolean => {
  // First check search term as it's commonly used
  if (!matchesSearchTerm(lead, searchTerm)) {
    return false;
  }

  // Lead quality filter - important for basic filtering
  if (!matchesQualityFilter(lead, filters.leadQuality)) {
    return false;
  }

  // Then check status, outcome, and type filters
  if (!matchesStatusFilter(lead, filters.statuses)) {
    return false;
  }

  if (!matchesOutcomeFilter(lead, filters.outcomes)) {
    return false;
  }

  if (!matchesTypeFilter(lead, filters.types)) {
    return false;
  }

  // Check numeric value filters
  if (!matchesValueFilter(lead, filters.minValue, filters.maxValue)) {
    return false;
  }

  // Check date filters
  if (!matchesDateFilter(lead, filters.dateRange.from, filters.dateRange.to)) {
    return false;
  }

  return true;
};
